import React, { useRef, useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  useTheme,
  useColumnResizing,
  useFilters,
  useTableSort,
  useSearch,
  usePagination,
  useMediaQuery,
  useProductMode,
} from "./hooks";
import { useCloudTableData } from "./hooks/useCloudTableData";
import { useTaskRunPolling } from "./hooks/useTaskRunPolling";
import { useCloudExport } from "./hooks/useCloudExport";
import { useImageDownload } from "./hooks/useImageDownload";
import { useCloudColumnEditing } from "./hooks/useCloudColumnEditing";
import { useCloudRowDeletion } from "./hooks/useCloudRowDeletion";
import { useAuth } from "../../contexts/AuthContext";

// Import components
import TableHeader from "./components/TableHeader";
import FiltersPanel from "./components/FiltersPanel";
import LoadingState from "./components/LoadingState";
import ErrorState from "./components/ErrorState";
import EmptyState from "./components/EmptyState";
import TableComponent from "./components/TableComponent";
import StylesComponent from "./components/StylesComponent";
import PaginationControls from "./components/PaginationControls";
import ExportMenu from "./components/ExportMenu";
import ImageDownloadModal from "./components/ImageDownloadModal";
import ProductView from "./components/ProductView";
import ProductModeSettings from "./components/ProductModeSettings";
import ImageGalleryModal from "../Dashboard/AutomationsView/components/ImageGalleryModal";
import { expandImageUrls } from "./utils/renderUtils";
import {
  collectRowGalleryImages,
  findAdjacentGalleryRow,
  clampGalleryImageIndex,
  resolveRowGalleryTitle,
} from "./utils/galleryNavigation";
import { cfFullImage } from "../../utils/imageUtils";
import { MOBILE_MEDIA_QUERY } from "./constants";

const CloudDataTableView = () => {
  // Get URL parameters from React Router
  const { tableId: tableIdParam } = useParams();
  const navigate = useNavigate();

  // Auth context for user info
  const { user, isAdmin } = useAuth();

  // Get filter state and handlers
  const { filters, showFilters, toggleFilter, handleToggleFilters } = useFilters();

  // Get theme state and toggle function
  const { isDarkMode, toggleTheme } = useTheme();

  // Search functionality - manages search term state (server handles actual filtering)
  const { searchTerm, setSearchTerm } = useSearch();

  // Get cloud table data - pass searchTerm for server-side filtering
  const { tableId, tableHeader, columns, setColumns, data, loading, isPaginating, error, errorStatusCode, processedData, pagination, refreshData, goToPage, removeRowsLocal, noTableSelected, isSearching } =
    useCloudTableData(filters, tableIdParam || null, searchTerm);

  // Poll for active task run status and auto-refresh when complete
  const { isActive: isTaskRunActive, status: taskRunStatus, startedAt: taskRunStartedAt } = useTaskRunPolling(
    tableHeader?.taskRunId,
    refreshData
  );

  // Column editing functionality
  const {
    columnMenuOpen,
    setColumnMenuOpen,
    columnMenuRef,
    handleColumnMenuToggle,
    editingColumnId,
    setEditingColumnId,
    editColumnName,
    setEditColumnName,
    editColumnType,
    setEditColumnType,
    handleColumnEditStart,
    handleColumnEditSave,
    deletingColumn,
    handleColumnDelete,
    deletedColumnName,
    clearDeleteFeedback,
    columnTypeOptions,
  } = useCloudColumnEditing({ tableId, columns, setColumns });

  // Row deletion functionality (works on archived tables too — the backend
  // rewrites the table's parquet file without the deleted rows)
  const { deletingRowId, handleRowDelete, rowDeleteFeedback, clearRowDeleteFeedback } = useCloudRowDeletion({
    tableId,
    removeRowsLocal,
    refreshData,
  });

  // Table sorting (pass pagination for correct row index offsets)
  const { sortConfig, sortedData, requestSort } = useTableSort(processedData, pagination);

  // Client-side pagination for sorted data (server already handles search filtering)
  const { paginatedData, resetPagination } = usePagination(sortedData?.processedData || [], 200);

  // Export functionality - using sorted data (displayed data only) with original columns
  // Pass original `columns` (not processedData.displayColumns) so exports include all columns regardless of collapsed state
  // Pass hideImages flag to exclude image columns when the "hide images" filter is enabled
  const { handleExportCSV, handleExportExcel, handleExportJSON, handleExportShopify, handleCopyToClipboard, handleExportToGoogleSheets, isExporting, exportingFormat, exportMessage, canExport, exportStats, clipboardDisabled, clipboardDisabledReason, googleSheetsStatus, fetchGoogleSheetsStatus } = useCloudExport(
    sortedData?.processedData || [],
    data,
    columns,
    tableHeader,
    null, // onExportSuccess callback
    pagination.total, // totalRowCount for clipboard limit check
    !filters.includeImages, // hideImages - exclude image columns when images are hidden
    !filters.showDebugColumns // hideDebugColumns - exclude debug columns unless the user revealed them
  );

  // Product mode: on phones, show the rows as a product sheet (or an image
  // grid) built to be screen-recorded. Settings persist per table.
  const isMobile = useMediaQuery(MOBILE_MEDIA_QUERY);
  const productMode = useProductMode({ tableId, columns, data, filters, isMobile });

  // With the export card armed, file exports wait until the card has gone, so
  // the browser's download prompt follows the confirmation instead of cutting
  // into it. Clipboard and Google Sheets run at once under the card: a
  // clipboard write or a new tab delayed past the tap that asked for it can
  // be blocked by the browser.
  const { exportAnimationArmed, playExport } = productMode;
  const withExportCard = useCallback(
    (run, format, { defer = false } = {}) =>
      () => {
        if (!exportAnimationArmed) return run();
        if (defer) {
          playExport(format, run);
          return undefined;
        }
        playExport(format);
        return run();
      },
    [exportAnimationArmed, playExport]
  );

  // What the product view's strip says the data came from.
  const productSource = useMemo(() => {
    const sourceTask = tableHeader?.metadata?.sourceTask;
    return {
      name: sourceTask?.name || null,
      url: sourceTask?.url || tableHeader?.metadata?.url || null,
      favicon: sourceTask?.favicon || null,
    };
  }, [tableHeader]);

  // Image download functionality
  const {
    isModalOpen: isImageDownloadOpen,
    openModal: openImageDownload,
    closeModal: closeImageDownload,
    imageColumns,
    canDownload: canDownloadImages,
    downloadState,
    handleDownload: handleImageDownload,
    getPreviewImages,
    getEstimatedCount,
  } = useImageDownload(tableHeader, columns, data, pagination.total);

  // State for selected row
  const [selectedRowId, setSelectedRowId] = useState(null);

  // Handle row selection (toggle on click)
  const handleRowSelect = useCallback((rowId) => {
    setSelectedRowId((prevId) => (prevId === rowId ? null : rowId));
  }, []);

  // State for image gallery modal. rowId/columnId keep up/down navigation
  // scoped to the loaded page and the column the gallery was opened from.
  const [galleryState, setGalleryState] = useState({
    isOpen: false,
    images: [],
    initialIndex: 0,
    rowId: null,
    columnId: null,
    rowLabel: null,
  });
  const galleryStateRef = useRef(galleryState);
  galleryStateRef.current = galleryState;

  const resolveGalleryColumn = useCallback(
    (columnId) => {
      const displayColumns = processedData?.displayColumns || [];
      return (
        displayColumns.find((col) => col.id === columnId) ||
        displayColumns.find((col) => col.id === "image_previews" || col.type === "image_group_toggle") ||
        null
      );
    },
    [processedData?.displayColumns]
  );

  const getGalleryImagesForRow = useCallback(
    (row, columnId) => {
      // A product tile opens every distinct image of its row, whatever the
      // table's image filter says, so up/down has to walk the same lists.
      if (productMode.isActive) return productMode.getRowImages(row);
      const column = resolveGalleryColumn(columnId);
      return collectRowGalleryImages(row, column, processedData?.displayColumns || [], expandImageUrls);
    },
    [productMode.isActive, productMode.getRowImages, resolveGalleryColumn, processedData?.displayColumns]
  );

  // Handler to open gallery
  const handleOpenGallery = useCallback((images, initialIndex = 0, row = null, column = null) => {
    if (!images?.length) return;
    const next = {
      isOpen: true,
      images,
      initialIndex,
      rowId: row?.id ?? null,
      columnId: column?.id ?? null,
      rowLabel: row?.displayIndex != null ? `Row ${row.displayIndex}` : null,
    };
    galleryStateRef.current = next;
    setGalleryState(next);
    if (row?.id != null) setSelectedRowId(row.id);
  }, []);

  // A row or tile opens the gallery on the image it shows (or on `url`, when a
  // record's thumbnail was tapped), with every image of the row available so
  // any of them can be made the cover.
  const handleOpenProductGallery = useCallback(
    (row, url = null) => {
      const images = productMode.getRowImages(row);
      if (!images.length) return;
      const start = url || productMode.resolveTile(row).url;
      handleOpenGallery(images, Math.max(0, images.indexOf(start)), row, null);
    },
    [productMode.getRowImages, productMode.resolveTile, handleOpenGallery]
  );

  const galleryCover = useMemo(() => {
    if (!productMode.isActive || !galleryState.isOpen || galleryState.rowId == null) return null;
    const row = paginatedData.find((item) => String(item.id) === String(galleryState.rowId));
    if (!row) return null;
    return {
      url: productMode.resolveTile(row).url,
      onSelect: (url) => productMode.setCover(row.id, url),
    };
  }, [productMode.isActive, productMode.resolveTile, productMode.setCover, galleryState.isOpen, galleryState.rowId, paginatedData]);

  // Handler to close gallery
  const handleCloseGallery = useCallback(() => {
    setGalleryState((prev) => ({ ...prev, isOpen: false }));
  }, []);

  // Up/down: next loaded row that has images in the same column context.
  // Updates the ref synchronously so key-repeat does not stall on one row.
  const handleGalleryRowNavigate = useCallback(
    (direction, currentImageIndex = 0) => {
      const prev = galleryStateRef.current;
      if (!prev?.rowId) return;
      const target = findAdjacentGalleryRow(paginatedData, prev.rowId, direction, (row) =>
        getGalleryImagesForRow(row, prev.columnId)
      );
      if (!target) return;
      const next = {
        isOpen: true,
        images: target.images,
        initialIndex: clampGalleryImageIndex(currentImageIndex, target.images.length),
        rowId: target.row.id,
        columnId: prev.columnId,
        rowLabel: target.row.displayIndex != null ? `Row ${target.row.displayIndex}` : null,
      };
      galleryStateRef.current = next;
      setGalleryState(next);
      setSelectedRowId(target.row.id);
    },
    [paginatedData, getGalleryImagesForRow]
  );

  const galleryCanGoUp = useMemo(
    () =>
      Boolean(
        galleryState.isOpen &&
          galleryState.rowId &&
          findAdjacentGalleryRow(paginatedData, galleryState.rowId, -1, (row) =>
            getGalleryImagesForRow(row, galleryState.columnId)
          )
      ),
    [galleryState.isOpen, galleryState.rowId, galleryState.columnId, paginatedData, getGalleryImagesForRow]
  );

  const galleryCanGoDown = useMemo(
    () =>
      Boolean(
        galleryState.isOpen &&
          galleryState.rowId &&
          findAdjacentGalleryRow(paginatedData, galleryState.rowId, 1, (row) =>
            getGalleryImagesForRow(row, galleryState.columnId)
          )
      ),
    [galleryState.isOpen, galleryState.rowId, galleryState.columnId, paginatedData, getGalleryImagesForRow]
  );

  const galleryTitle = useMemo(() => {
    if (!galleryState.isOpen || !galleryState.rowId) return "Image Gallery";
    const row = paginatedData.find((item) => String(item.id) === String(galleryState.rowId));
    return resolveRowGalleryTitle(row, processedData?.displayColumns || []);
  }, [galleryState.isOpen, galleryState.rowId, paginatedData, processedData?.displayColumns]);

  const galleryRowNavigation = useMemo(() => {
    if (!galleryState.isOpen || galleryState.rowId == null) return null;
    return {
      onNavigate: handleGalleryRowNavigate,
      canGoUp: galleryCanGoUp,
      canGoDown: galleryCanGoDown,
      label: galleryState.rowLabel,
    };
  }, [
    galleryState.isOpen,
    galleryState.rowId,
    galleryState.rowLabel,
    handleGalleryRowNavigate,
    galleryCanGoUp,
    galleryCanGoDown,
  ]);


  // Table refs
  const tableRef = useRef(null);
  const tableContainerRef = useRef(null);
  const tableWrapperRef = useRef(null);
  const tableScrollRef = useRef(null);

  // Column resizing (read-only mode - no saving to backend)
  const { columnWidths, resizingColumn, handleResizeMouseDown } = useColumnResizing();

  // Reset pagination when table or filters change
  useEffect(() => {
    resetPagination();
  }, [tableId, filters, searchTerm, resetPagination]);

  // Keep the table scrolled to the gallery row so closing lands on the right place.
  useEffect(() => {
    if (!galleryState.isOpen || galleryState.rowId == null) return;
    const root = tableScrollRef.current;
    if (!root) return;
    const rowEl = root.querySelector(`[data-row-id="${CSS.escape(String(galleryState.rowId))}"]`);
    rowEl?.scrollIntoView({ block: "nearest" });
  }, [galleryState.isOpen, galleryState.rowId]);

  // Preload the first image of the adjacent rows so up/down feels instant.
  useEffect(() => {
    if (!galleryState.isOpen || galleryState.rowId == null) return;
    for (const direction of [-1, 1]) {
      const adjacent = findAdjacentGalleryRow(paginatedData, galleryState.rowId, direction, (row) =>
        getGalleryImagesForRow(row, galleryState.columnId)
      );
      const url = adjacent?.images?.[0];
      if (url) {
        const preload = new Image();
        preload.src = cfFullImage(url);
      }
    }
  }, [galleryState.isOpen, galleryState.rowId, galleryState.columnId, paginatedData, getGalleryImagesForRow]);

  // Scroll to top when page changes
  useEffect(() => {
    tableScrollRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  }, [pagination.page]);

  // Update page title when table header changes
  useEffect(() => {
    const title = tableHeader?.metadata?.sourceTask?.name;
    if (title) {
      document.title = `Cloud Table | ${title}`;
    } else {
      document.title = 'Cloud Table';
    }

    return () => {
      document.title = 'Cloud Table';
    };
  }, [tableHeader?.metadata?.sourceTask?.name]);

  // Handle table selection from extraction history dropdown
  const handleTableSelect = useCallback((selectedTableId) => {
    navigate(`/table/${selectedTableId}`);
  }, [navigate]);

  return (
    <div className="h-full flex flex-col text-white bg-[#0a0a0b]" style={{ height: "100vh" }}>
      {/* Header */}
      <TableHeader
        selectedTableDataId={tableId}
        loading={loading}
        toggleTheme={toggleTheme}
        isDarkMode={isDarkMode}
        automationRunning={isTaskRunActive}
        autoRefreshEnabled={isTaskRunActive}
        lastAutoRefresh={null}
        userEmail={user?.email}
        tableData={tableHeader}
        data={data}
        filteredData={sortedData?.processedData || []}
        filters={filters}
        toggleFilter={toggleFilter}
        totalRows={pagination.total}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        isSearching={isSearching}
        taskId={tableHeader?.taskId}
        onTableSelect={handleTableSelect}
        onOpenImageDownload={openImageDownload}
        canDownloadImages={canDownloadImages}
        taskRunStatus={taskRunStatus}
        taskRunStartedAt={taskRunStartedAt}
        isAdmin={isAdmin}
        showProductModeToggle={productMode.canActivate}
        isProductMode={productMode.isActive}
        onToggleProductMode={productMode.toggle}
        exportMenu={
          <ExportMenu
            onExportCSV={withExportCard(handleExportCSV, "csv", { defer: true })}
            onExportExcel={withExportCard(handleExportExcel, "excel", { defer: true })}
            onExportJSON={withExportCard(handleExportJSON, "json", { defer: true })}
            onExportShopify={withExportCard(handleExportShopify, "shopify", { defer: true })}
            onCopyToClipboard={withExportCard(handleCopyToClipboard, "clipboard")}
            onExportToGoogleSheets={withExportCard(handleExportToGoogleSheets, "googlesheets")}
            onExportMenuOpened={fetchGoogleSheetsStatus}
            isExporting={isExporting}
            exportingFormat={exportingFormat}
            exportMessage={exportMessage}
            canExport={canExport}
            exportStats={exportStats}
            isDarkMode={true}
            tableData={tableHeader}
            totalRowCount={pagination.total}
            clipboardDisabled={clipboardDisabled}
            clipboardDisabledReason={clipboardDisabledReason}
            googleSheetsStatus={googleSheetsStatus}
            isShopifyTable={tableHeader?.metadata?.specificExtractionType === "shopify"}
          />
        }
      />

      {/* Table content */}
      <div
        className={`flex-1 overflow-auto relative bg-[#0a0a0b] ${productMode.isActive ? "p-0" : "p-4"}`}
        ref={(el) => {
          tableRef.current = el;
          tableContainerRef.current = el;
        }}
        style={{ flexGrow: 1, display: "flex", flexDirection: "column", position: "relative" }}
      >
        {loading && data.length === 0 ? (
          <LoadingState isDarkMode={true} />
        ) : noTableSelected ? (
          <EmptyState type="noTableSelected" isDarkMode={true} />
        ) : error ? (
          <ErrorState error={error} statusCode={errorStatusCode} isDarkMode={true} />
        ) : data.length === 0 && !searchTerm ? (
          <EmptyState type="noData" isDarkMode={true} />
        ) : data.length === 0 && searchTerm ? (
          <EmptyState type="noMatchingData" isDarkMode={true} />
        ) : productMode.isActive ? (
          <ProductView
            ref={tableScrollRef}
            rows={paginatedData}
            settings={productMode.settings}
            fields={productMode.activeFields}
            priceColumnId={productMode.priceColumn?.id ?? null}
            recordColumns={productMode.recordColumns}
            resolveTile={productMode.resolveTile}
            isDarkMode={isDarkMode}
            isPaginating={isPaginating}
            selectedRowId={selectedRowId}
            onOpenGallery={handleOpenProductGallery}
            onOpenSettings={productMode.openSettings}
            source={productSource}
            totalRows={pagination.total}
            revealKey={`${tableId}:${pagination.page}:${productMode.introEpoch}`}
            intro={
              productMode.introRun
                ? {
                    runKey: productMode.introRun,
                    durationMs: productMode.introSettings.durationSec * 1000,
                    onLeave: productMode.leaveIntro,
                    onDone: productMode.finishIntro,
                  }
                : null
            }
            exportAnimation={
              productMode.exportRun
                ? {
                    runKey: productMode.exportRun.key,
                    format: productMode.exportRun.format,
                    durationMs: productMode.exportSettings.durationSec * 1000,
                    onDone: productMode.finishExport,
                  }
                : null
            }
          />
        ) : (
          <div
            className="flex-1 w-full rounded-xl overflow-hidden bg-[#111113] border border-white/[0.06] shadow-2xl shadow-black/40"
            ref={tableWrapperRef}
            style={{
              position: "relative",
              height: "100%",
            }}
          >
            <TableComponent
              ref={tableScrollRef}
              displayData={paginatedData}
              processedData={processedData}
              isDarkMode={isDarkMode}
              isPaginating={isPaginating}
              previousDataLength={0} // No new row animations in cloud mode
              newRowsCount={0}
              columnWidths={columnWidths}
              resizingColumn={resizingColumn}
              handleResizeMouseDown={handleResizeMouseDown}
              sortConfig={sortConfig}
              requestSort={requestSort}
              draggedColumn={null} // No drag-drop in cloud mode
              handleColumnDragStart={null}
              handleColumnDragOver={null}
              handleColumnDragLeave={null}
              handleColumnDrop={null}
              columnMenuOpen={columnMenuOpen}
              setColumnMenuOpen={setColumnMenuOpen}
              editingColumnId={editingColumnId}
              editColumnName={editColumnName}
              editColumnType={editColumnType}
              columnMenuRef={columnMenuRef}
              setEditingColumnId={setEditingColumnId}
              setEditColumnName={setEditColumnName}
              setEditColumnType={setEditColumnType}
              handleColumnMenuToggle={handleColumnMenuToggle}
              handleColumnEditStart={handleColumnEditStart}
              handleColumnEditSave={handleColumnEditSave}
              toggleFilter={toggleFilter}
              filters={filters}
              handleColumnMerge={null} // No column merge in cloud mode
              deletingColumn={deletingColumn}
              handleColumnDelete={handleColumnDelete}
              columns={columns}
              editingCell={null} // No cell editing in cloud mode
              editValue=""
              setEditValue={null}
              handleCellEdit={null}
              handleCellEditCancel={null}
              handleCellEditSave={null}
              automationActive={isTaskRunActive}
              automationRunning={isTaskRunActive}
              columnTypeOptions={columnTypeOptions}
              selectedRowId={selectedRowId}
              onRowSelect={handleRowSelect}
              onOpenGallery={handleOpenGallery}
              deletedColumnName={deletedColumnName}
              onDeleteFeedbackComplete={clearDeleteFeedback}
              onRowDelete={handleRowDelete}
              deletingRowId={deletingRowId}
              rowDeleteFeedback={rowDeleteFeedback}
              onRowDeleteFeedbackComplete={clearRowDeleteFeedback}
            />
          </div>
        )}
      </div>

      {/* Server-side Cloud Pagination */}
      {!error && data.length > 0 && pagination.totalPages > 1 && (
        <PaginationControls
          pagination={pagination}
          goToPage={goToPage}
          isDarkMode={true}
          isLoading={loading || isPaginating}
        />
      )}

      {/* Filters panel as centered modal */}
      <FiltersPanel
        showFilters={showFilters}
        filters={filters}
        toggleFilter={toggleFilter}
        isDarkMode={true}
        sortedData={sortedData}
        data={data}
        processedData={processedData}
        columns={columns}
        handleToggleFilters={handleToggleFilters}
      />

      {/* Styles */}
      <StylesComponent isDarkMode={isDarkMode} />

      {/* Product mode settings (bottom sheet) */}
      <ProductModeSettings
        isOpen={productMode.isSettingsOpen}
        onClose={productMode.closeSettings}
        isDarkMode={isDarkMode}
        settings={productMode.settings}
        updateSettings={productMode.updateSettings}
        onReset={productMode.resetSettings}
        imageColumns={productMode.imageColumns}
        fieldColumns={productMode.fieldColumns}
        activeFields={productMode.activeFields}
        maxImagesPerRow={productMode.maxImagesPerRow}
        introSettings={productMode.introSettings}
        updateIntroSettings={productMode.updateIntroSettings}
        onPlayIntro={productMode.playIntro}
        exportSettings={productMode.exportSettings}
        updateExportSettings={productMode.updateExportSettings}
        onPlayExport={productMode.playExport}
      />

      {/* Image Gallery Modal */}
      {galleryState.isOpen && (
        <ImageGalleryModal
          screenshots={galleryState.images}
          title={galleryTitle}
          initialIndex={galleryState.initialIndex}
          onClose={handleCloseGallery}
          rowNavigation={galleryRowNavigation}
          cover={galleryCover}
        />
      )}

      {/* Image Download Modal */}
      {isImageDownloadOpen && (
        <ImageDownloadModal
          isOpen={isImageDownloadOpen}
          onClose={closeImageDownload}
          imageColumns={imageColumns}
          columns={columns}
          currentPageData={data}
          tableHeader={tableHeader}
          totalRows={pagination.total}
          isDarkMode={true}
          downloadState={downloadState}
          onDownload={handleImageDownload}
          getPreviewImages={getPreviewImages}
          getEstimatedCount={getEstimatedCount}
        />
      )}
    </div>
  );
};

export default CloudDataTableView;
